import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import 'swiper/css/bundle';
import {
  salePriceLabelHTML,
  comparePriceHTML,
  setSoldOutLabel,
} from './card-a11y.js';

/**
 * ProductsGrid Component
 * Handles product grid interactions, modal display, and cart functionality
 * Refactored to use vanilla JavaScript instead of jQuery
 */
class ProductsGrid extends HTMLElement {
  // ============================================================================
  // CONSTANTS - Static properties for selectors, classes, and default values
  // ============================================================================
  static SELECTORS = {
    PRODUCT_CTA: '.product-cta',
    AVAILABLE_SIZE: '.available-size',
    REBUY_PRODUCT_ACTIONS: '.rebuy-product-actions',
    REBUY_PRODUCT_BLOCK: '.rebuy-product-block',
    PRODUCT_MODAL: '.product-modal',
    CLOSE_MODAL: '.close-product-modal',
    MODAL_INNER: '.product-modal__inner',
    MODAL_CTA: '.product-modal__cta',
    MODAL_SIZE_OPTION: '.product-modal__size-option',
    MODAL_OPTIONS_WRAP: '.product-modal__options-wrap',
    MODAL_OPTION_COLOR: '.product-modal__option-color',
    MODAL_OPTION_SIZE: '.product-modal__option-size',
    SWIPER_SLIDE: '.swiper-slide',
    MODAL_GALLERY: '.product-modal__gallery',
    MODAL_BUTTON_PREV: '.modal-button-prev',
    MODAL_BUTTON_NEXT: '.modal-button-next',
  };

  static CSS_CLASSES = {
    ACTIVE: 'active',
    DISABLED: 'disabled',
    HIDDEN: 'hidden',
  };

  static DEFAULT_TEXT = {
    ADD_TO_CART: 'add to cart',
    SELECT_SIZE: 'select size',
    NOTIFY_WHEN_AVAILABLE: 'Notify Me When Available',
    VIEW_FULL_DETAILS: 'View Full Details',
  };

  static BREAKPOINTS = {
    DESKTOP: 1024,
  };

  // ============================================================================
  // CONSTRUCTOR - Initialize component properties
  // ============================================================================
  constructor() {
    super();
    this.accessToken = '8bcb2ea2ba9acd5b735e7abe4cbca7d0';
    this.graphUrl =
      'https://liquiddeath.myshopify.com/api/2025-07/graphql.json';
    this.selectedColor = null;
    this.market = this.getAttribute('data-market');
    this.isSubmitting = false;
    this.context = this.getAttribute('data-market').toUpperCase();
    this.currency = this.getAttribute('data-currency');
  }
  // ============================================================================
  // LIFECYCLE METHODS - Component initialization and event setup
  // ============================================================================
  connectedCallback() {
    this.setupEventListeners();
    this.initFlickityDragFix();
  }

  /**
   * Fix Flickity carousel drag vs click sensitivity
   * Prevents accidental clicks when user is trying to drag/swipe
   */
  initFlickityDragFix() {
    let isDragging = false;
    let dragStartX = 0;
    let dragStartY = 0;
    const DRAG_THRESHOLD = 10; // Pixels of movement before considered a drag

    const initDragListeners = () => {
      const flickityEl = this.querySelector('.flickity-enabled');
      if (!flickityEl) {
        // Retry if Flickity not yet initialized (Rebuy loads async)
        setTimeout(initDragListeners, 500);
        return;
      }

      // Track pointer down position
      flickityEl.addEventListener('pointerdown', (e) => {
        isDragging = false;
        dragStartX = e.clientX;
        dragStartY = e.clientY;
      }, { passive: true });

      // Detect if movement exceeds threshold
      flickityEl.addEventListener('pointermove', (e) => {
        if (dragStartX === 0 && dragStartY === 0) return;
        const deltaX = Math.abs(e.clientX - dragStartX);
        const deltaY = Math.abs(e.clientY - dragStartY);
        if (deltaX > DRAG_THRESHOLD || deltaY > DRAG_THRESHOLD) {
          isDragging = true;
        }
      }, { passive: true });

      // Reset on pointer up
      flickityEl.addEventListener('pointerup', () => {
        // Delay reset to allow click event to be intercepted
        setTimeout(() => {
          isDragging = false;
          dragStartX = 0;
          dragStartY = 0;
        }, 100);
      }, { passive: true });

      // Intercept clicks on product links if dragging
      flickityEl.addEventListener('click', (e) => {
        if (isDragging) {
          const link = e.target.closest('a.clickable, a.rebuy-product-title-link, a.rebuy-product-image');
          if (link) {
            e.preventDefault();
            e.stopPropagation();
          }
        }
      }, true); // Use capture phase to intercept before Rebuy handlers
    };

    // Start checking for Flickity
    initDragListeners();
  }

  /**
   * Set up all event listeners for the component
   */
  setupEventListeners() {
    // Accessibility: Handle keyboard navigation for product CTAs
    this.addEventListener('keyup', this.handleKeyup.bind(this));

    // Main click handler for all product interactions
    this.addEventListener('click', this.handleClick.bind(this));
  }

  /**
   * Handle keyboard events for accessibility
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyup(event) {
    const target = event.target;

    // Handle product CTA keyboard navigation
    if (
      target.classList.contains(
        ProductsGrid.SELECTORS.PRODUCT_CTA.replace('.', '')
      )
    ) {
      if (
        event.code === 'NumpadEnter' ||
        event.code === 'Enter' ||
        event.code === 'Space'
      ) {
        event.target.click();
        setTimeout(() => {
          const modalInner = document.querySelector(
            ProductsGrid.SELECTORS.MODAL_INNER
          );
          if (modalInner) {
            modalInner.focus();
          }
        }, 300);
      }
    }
  }

  /**
   * Handle click events for product interactions
   * @param {MouseEvent} event - The click event
   */
  handleClick(event) {
    const target = event.target;

    // Handle product CTA clicks (opens product modal)
    if (
      target.classList.contains(
        ProductsGrid.SELECTORS.PRODUCT_CTA.replace('.', '')
      ) &&
      !target.classList.contains(
        ProductsGrid.SELECTORS.AVAILABLE_SIZE.replace('.', '')
      )
    ) {
      const handle = target.dataset.handle;
      this.selectedColor = target.dataset.color || null;
      this.fetchProduct(handle);

      setTimeout(() => {
        this.setupModalAccessibility();
      }, 500);
    }

    // Handle rebuy product action clicks
    if (
      target.classList.contains(
        ProductsGrid.SELECTORS.REBUY_PRODUCT_ACTIONS.replace('.', '')
      )
    ) {
      const rebuyBlock = target.closest(
        ProductsGrid.SELECTORS.REBUY_PRODUCT_BLOCK
      );
      const handle = rebuyBlock.className.split(/\s+/)[1];
      this.selectedColor = null;
      this.fetchProduct(handle);
    }

    // Handle available size clicks (direct add to cart)
    if (
      target.classList.contains(
        ProductsGrid.SELECTORS.AVAILABLE_SIZE.replace('.', '')
      )
    ) {
      const variantId = target.dataset.id;
      this.addProductToCart(variantId);
    }
  }

  /**
   * Set up accessibility for modal labels
   */
  setupModalAccessibility() {
    const labels = document.querySelectorAll("label[tabindex='0']");

    labels.forEach((label) => {
      label.addEventListener('keyup', (event) => {
        if (
          event.code === 'NumpadEnter' ||
          event.code === 'Enter' ||
          event.code === 'Space'
        ) {
          event.target.click();
        }
      });
    });
  }

  /**
   * Add product directly to cart (for available size clicks)
   * @param {string} variantId - The variant ID to add to cart
   */
  async addProductToCart(variantId) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      // Optionally update the cart UI here or show a confirmation
    } catch (error) {
      console.error('Error adding product to cart:', error);
      // Handle errors, e.g., show a message to the user
    }
  }

  // ============================================================================
  // GRAPHQL METHODS - Product data fetching and query building
  // ============================================================================
  /**
   * Fetch product data from Shopify Storefront API
   * @param {string} handle - The product handle
   */
  async fetchProduct(handle) {
    try {
      const response = await fetch(this.graphUrl, {
        method: 'POST',
        headers: {
          'X-Shopify-Storefront-Access-Token': this.accessToken,
          'Content-Type': 'application/graphql',
        },
        body: this.buildProductQuery(handle),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      this.renderProduct(result.data.product);
    } catch (error) {
      console.error('Error fetching product:', error);
      this.handleFetchError(error);
    }
  }

  /**
   * Handle fetch errors
   * @param {Error} error - The error that occurred
   */
  handleFetchError(error) {
    console.error('Failed to fetch product data:', error);
    // Could add user-facing error handling here
  }

  /**
   * Build GraphQL query for product data
   * @param {string} handle - The product handle
   * @returns {string} The GraphQL query string
   */
  buildProductQuery(handle) {
    return `query @inContext(country: ${this.context}) {
      product(handle: "${handle}") {
        id
        handle
        title
        productType
        metafields(identifiers: [{namespace: "custom", key: "swatch"}, {namespace: "pdp", key: "amazon_link"}, {namespace: "pdp", key: "amazon_button_text"}]) {
          value
        }
        featuredImage {
          url
        }
        images(first: 100) {
          edges {
            node {
              url
            }
          }
        }
        variants(first: 100) {
          edges {
            node {
              id
              price {
                amount
              }
              compareAtPrice {
                amount
              }
              title
              availableForSale
              quantityAvailable
              image {
                url
              }
            }
          }
        }
        options {
          name
          values
        }
      }
    }`;
  }

  // ============================================================================
  // RENDERING METHODS - Product modal display and HTML generation
  // ============================================================================
  /**
   * Render the product modal with all options and functionality
   * @param {Object} product - The product data from GraphQL
   */
  renderProduct(product) {
    // Get button text from metafields or use default
    let buttonText = ProductsGrid.DEFAULT_TEXT.ADD_TO_CART;
    if (product.metafields[2] != null) {
      buttonText = product.metafields[2].value;
    }

    let html = this.buildModalHTML(product, buttonText);

    // Append modal to body
    document.body.insertAdjacentHTML('beforeend', html);

    // Set up modal functionality
    this.setupModalFunctionality(product, buttonText);
  }

  /**
   * Build the modal HTML structure
   * @param {Object} product - The product data
   * @param {string} buttonText - The button text to display
   * @returns {string} The complete modal HTML
   */
  buildModalHTML(product, buttonText) {
    let html = `
            <div class="product-modal">
                <div class="product-modal__overlay close-product-modal"></div>
                <div class="product-modal__inner" tabindex="0" aria-label="Add to cart modal">
                    <button type="button" class="close-product-modal absolute top-4 right-4 cursor-pointer" aria-label="Close add to cart modal">
                        <svg xmlns='http://www.w3.org/2000/svg' width='25' height='25' viewBox='0 0 33 33' fill='none'>
                            <path d="M24.75 8.25L8.75 24.25" stroke="#000" stroke-width="1.5" stroke-linecap="square"/>
                            <path d="M8.75 8.25L24.75 24.25" stroke="#000" stroke-width="1.5" stroke-linecap="square"/>
                        </svg>
                    </button>`;

    // Add gallery for desktop
    if (window.innerWidth > ProductsGrid.BREAKPOINTS.DESKTOP) {
      html += this.buildGalleryHTML(product);
    }

    // Add price and title section
    html += this.buildPriceAndTitleHTML(product);

    // Add product options
    html += this.buildOptionsHTML(product);

    // Add market-specific URL
    let market = this.market;
    if (market === 'us') {
      market = '/';
    } else {
      market = '/en-' + market + '/';
    }

    // Complete the modal HTML
    html += this.buildModalFooterHTML(product, buttonText, market);

    return html;
  }

  /**
   * Build gallery HTML for desktop
   * @param {Object} product - The product data
   * @returns {string} The gallery HTML
   */
  buildGalleryHTML(product) {
    let html = `<div class="relative">
                        <div class="product-modal__gallery relative">
                            <div class="swiper-wrapper">`;

    product.images.edges.forEach((image) => {
      html += `<div class="swiper-slide">
                                    <img src="${image.node.url}" alt="Product Gallery Image" />
                                </div>`;
    });

    html += `</div>
                        </div>
                        <button class='modal-button-prev' aria-label="Gallery arrow previous" style="border: none">
        <svg xmlns='http://www.w3.org/2000/svg' width='10' height='18' viewBox='0 0 10 18' fill='none'>
                            <path d="M2 16L8.5 9L2 2" stroke="#000" stroke-width="1.5" stroke-linecap="square"/>
                        </svg>
                        </button>
                        <button class='modal-button-next' aria-label="Gallery arrow next" style="border: none">
        <svg xmlns='http://www.w3.org/2000/svg' width='10' height='18' viewBox='0 0 10 18' fill='none'>
                            <path d="M2 16L8.5 9L2 2" stroke="#000" stroke-width="1.5" stroke-linecap="square"/>
                        </svg>
                        </button>
                        </div>`;

    return html;
  }

  /**
   * Build price and title HTML
   * @param {Object} product - The product data
   * @returns {string} The price and title HTML
   */
  buildPriceAndTitleHTML(product) {
    const variantPrice = Number(product.variants.edges[0].node.price.amount);
    let displayPrice = variantPrice;
    let comparePrice = '';

    // Check if this product is excluded from promo
    const isExcluded =
      window.promoExcludedProducts?.includes(product.handle) || false;

    // Apply promotional pricing if enabled and not excluded
    if (
      window.showPromoOriginalPrice &&
      window.promoDiscountPercentage > 0 &&
      !isExcluded
    ) {
      const discountMultiplier = (100 - window.promoDiscountPercentage) / 100;
      displayPrice = variantPrice * discountMultiplier;

      // If compare_at_price exists, use it; otherwise show original price as slashed
      if (product.variants.edges[0].node.compareAtPrice) {
        comparePrice = comparePriceHTML(
          `$${Number(product.variants.edges[0].node.compareAtPrice.amount).toFixed(2)}`
        );
      } else {
        comparePrice = comparePriceHTML(
          `$${variantPrice.toFixed(2)}`,
          'data-promo-calculated="true"'
        );
      }
    }
    // No promo - show regular pricing with compare_at_price if exists
    else if (product.variants.edges[0].node.compareAtPrice) {
      comparePrice = comparePriceHTML(
        `$${Number(product.variants.edges[0].node.compareAtPrice.amount).toFixed(2)}`
      );
    }

    return `<div class="flex justify-between items-center mt-6 product-modal__title-wrap">
                        <div class="product-modal__title text-dark uppercase">${
                          product.title
                        }</div>
      <div class="product-modal__price text-dark uppercase">
        ${comparePrice ? salePriceLabelHTML() : ''}${this.currency}${displayPrice.toFixed(2)} ${comparePrice}
      </div>
                    </div>
                    <div class="flex flex-col">`;
  }

  /**
   * Build options HTML for the modal
   * @param {Object} product - The product data
   * @returns {string} The options HTML
   */
  buildOptionsHTML(product) {
    let html = '';

    product.options.forEach((option) => {
      if (product.options[0].name !== 'Title') {
        html += this.buildOptionHTML(option, product);
      }
    });

    return html;
  }

  /**
   * Build individual option HTML
   * @param {Object} option - The option data
   * @param {Object} product - The product data
   * @returns {string} The option HTML
   */
  buildOptionHTML(option, product) {
    const optionClass =
      option.name.toLowerCase() !== 'color' &&
      option.name.toLowerCase() !== 'size'
        ? 'product-modal__option-size'
        : `product-modal__option-${option.name.toLowerCase()}`;

    const titleClass =
      option.name.toLowerCase() === 'size' && option.values.length === 1
        ? 'hidden'
        : '';

    const selectedColor =
      option.name.toLowerCase() === 'color'
        ? this.selectedColor ||
          product.options.filter((opt) => opt.name.toLowerCase() === 'color')[0]
            ?.values[0]
        : '';

    const titleId = `ModalOptionTitle-${option.name.replaceAll(' ', '-')}`;

    let html = `<div class="${optionClass}">
      <div class="product-modal__option-title text-dark ${titleClass}" id="${titleId}" data-name="${
        option.name
      }">
        ${
          option.name.toLowerCase() !== 'color' &&
          option.name.toLowerCase() !== 'size'
            ? ''
            : 'Select'
        } ${option.name}: 
        <span>${selectedColor}</span>
      </div>
                                <div role="radiogroup" aria-labelledby="${titleId}" class="product-modal__options-wrap product-modal__options-wrap-${option.name.toLowerCase()} flex flex-wrap items-center mt-4 ${
                                  option.name.toLowerCase() === 'size' &&
                                  option.values.length === 1
                                    ? 'mt-0'
                                    : ''
                                }">`;

    if (option.name.toLowerCase() !== 'color') {
      html += this.buildSizeOptionsHTML(option);
    } else {
      html += this.buildColorOptionsHTML(option, product);
    }

    html += `</div></div>`;

    // Add "Can't find your size?" link for size options only if there are unavailable variants
    if (option.name.toLowerCase() === 'size') {
      // Check if this product has any unavailable variants
      const hasUnavailableVariants = product.variants.edges.some(
        (variant) => !variant.node.availableForSale
      );
      if (hasUnavailableVariants) {
        html += this.buildCantFindSizeLinkHTML();
      }
    }

    return html;
  }

  /**
   * Build size options HTML
   * @param {Object} option - The size option data
   * @returns {string} The size options HTML
   */
  buildSizeOptionsHTML(option) {
    let html = '';

    option.values.forEach((size) => {
      const sizeId = size.replaceAll(' ', '-');
      const isChecked = option.values.length === 1 ? 'checked' : '';
      const labelClass = option.values.length === 1 ? 'hidden' : '';

      html += `<input type="radio" name="option-${option.name}" id="option-${sizeId}" data-name="${option.name}" value="${size}" class="sr-only" ${isChecked} />
        <label for="option-${sizeId}" class="${labelClass} product-modal__size-option text-base mb-2 text-dark border border-solid border-black p-1 min-w-[52px] m-[3px] h-10 flex items-center justify-center cursor-pointer" data-name="${size}">
          <span class="p-1 bg-white z-10">${size}</span>
        </label>`;
    });

    return html;
  }

  /**
   * Build "Can't find your size?" link HTML
   * @returns {string} The link HTML
   */
  buildCantFindSizeLinkHTML() {
    return `
      <div class="cant-find-size-link mb-4">
        <a href="#" class="text-base text-[#8a6d35] underline hover:no-underline cant-find-size-trigger cursor-pointer">
          Can't find your size?
        </a>
      </div>
    `;
  }

  /**
   * Build color options HTML
   * @param {Object} option - The color option data
   * @param {Object} product - The product data
   * @returns {string} The color options HTML
   */
  buildColorOptionsHTML(option, product) {
    let html = '';
    const selectedColor =
      this.selectedColor ||
      product.options.filter((opt) => opt.name.toLowerCase() === 'color')[0]
        ?.values[0];

    option.values.forEach((color) => {
      const colorVal = color
        .toLowerCase()
        .replaceAll(' ', '-')
        .replaceAll('/', '-');
      const isChecked = color === selectedColor ? 'checked' : '';

      html += `<input type="radio" name="option-${option.name}" ${isChecked} id="option-${colorVal}" data-name="${option.name}" value="${color}" class="sr-only" />
        <label for="option-${colorVal}" class="swatch h-5 w-5 mr-4 inline-block rounded-full cursor-pointer ${colorVal}" data-product="${product.title}" data-name="${option.name}" style="background: ${colorVal}"><span class="sr-only">${color}</span></label>`;
    });

    return html;
  }

  /**
   * Build modal footer HTML
   * @param {Object} product - The product data
   * @param {string} buttonText - The button text
   * @param {string} market - The market URL
   * @returns {string} The modal footer HTML
   */
  buildModalFooterHTML(product, buttonText, market) {
    const hasNonColorOptions =
      product.options.filter((option) => option.name.toLowerCase() !== 'color')
        .length > 0;
    const hasSizeOptions =
      product.options.filter((option) => option.name.toLowerCase() === 'size')
        .length > 0;
    const otherOptions = product.options.filter(
      (option) =>
        option.name.toLowerCase() !== 'size' &&
        option.name.toLowerCase() !== 'color'
    );

    const ctaClass = hasNonColorOptions ? '' : ProductsGrid.CSS_CLASSES.ACTIVE;
    const ctaText = hasNonColorOptions
      ? hasSizeOptions
        ? ProductsGrid.DEFAULT_TEXT.SELECT_SIZE
        : otherOptions[0]?.name || ''
      : buttonText;

    return `</div>
      <div class='animated-button-block-hero !block w-full mt-6'>
        <button class='product-modal__cta text-center button-white flex justify-center uppercase text-base w-full ${ctaClass}'>
          ${ctaText}
                        </button>
                    </div>
                    <div class="mt-6 flex justify-center">
        <a href="${market}products/${product.handle}" class="product-modal__details text-dark">
          ${ProductsGrid.DEFAULT_TEXT.VIEW_FULL_DETAILS}
        </a>
                    </div>
                </div>
            </div>`;
  }

  /**
   * Set up modal functionality after rendering
   * @param {Object} product - The product data
   * @param {string} buttonText - The button text
   */
  setupModalFunctionality(product, buttonText) {
    // Update CTA state
    this.updateCTAState(buttonText);

    // Check initial availability
    this.checkAvailability(product);

    // Initialize gallery for desktop
    let productGallery = null;
    if (window.innerWidth > ProductsGrid.BREAKPOINTS.DESKTOP) {
      productGallery = this.initializeGallery();
    }

    // Set up event listeners
    this.setupModalEventListeners(product, buttonText, productGallery);
  }

  /**
   * Update CTA button state
   * @param {string} buttonText - The button text
   */
  updateCTAState(buttonText) {
    const sizeOptionsChecked = document.querySelectorAll(
      '.product-modal__option-size input:checked'
    ).length;
    const ctaButton = document.querySelector('.product-modal__cta');

    if (sizeOptionsChecked && ctaButton) {
      ctaButton.textContent = buttonText;
      ctaButton.classList.add(ProductsGrid.CSS_CLASSES.ACTIVE);
    }
  }

  /**
   * Initialize Swiper gallery for desktop
   * @returns {Swiper} The Swiper instance
   */
  initializeGallery() {
    const galleryElement = document.querySelector(
      ProductsGrid.SELECTORS.MODAL_GALLERY
    );
    if (!galleryElement) return null;

    return new Swiper(galleryElement, {
      initialSlide: 0,
      loop: true,
      autoHeight: true,
      slidesPerView: 1,
      modules: [Navigation],
      navigation: {
        nextEl: ProductsGrid.SELECTORS.MODAL_BUTTON_NEXT,
        prevEl: ProductsGrid.SELECTORS.MODAL_BUTTON_PREV,
      },
    });
  }

  /**
   * Set up all modal event listeners
   * @param {Object} product - The product data
   * @param {string} buttonText - The button text
   * @param {Swiper} productGallery - The Swiper gallery instance
   */
  setupModalEventListeners(product, buttonText, productGallery) {
    // Close modal functionality
    this.setupCloseModalListeners();

    // Option change listeners
    this.setupOptionChangeListeners(product, buttonText, productGallery);

    // Add to cart listener
    this.setupAddToCartListener(product);

    // "Can't find your size?" link listener
    this.setupCantFindSizeLinkListener(product);
  }

  /**
   * Set up close modal event listeners
   */
  setupCloseModalListeners() {
    const closeButtons = document.querySelectorAll(
      ProductsGrid.SELECTORS.CLOSE_MODAL
    );

    closeButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const modal = document.querySelector(
          ProductsGrid.SELECTORS.PRODUCT_MODAL
        );
        if (modal) {
          modal.remove();
        }
      });

      button.addEventListener('keyup', (event) => {
        if (
          event.code === 'NumpadEnter' ||
          event.code === 'Enter' ||
          event.code === 'Space'
        ) {
          event.target.click();
        }
      });
    });
  }

  /**
   * Set up option change event listeners
   * @param {Object} product - The product data
   * @param {string} buttonText - The button text
   * @param {Swiper} productGallery - The Swiper gallery instance
   */
  setupOptionChangeListeners(product, buttonText, productGallery) {
    const optionInputs = document.querySelectorAll(
      '.product-modal__options-wrap input'
    );

    optionInputs.forEach((input) => {
      input.addEventListener('change', (event) => {
        const target = event.target;

        // Update option title
        this.updateOptionTitle(target);

        // Update CTA state
        this.updateCTAState(buttonText);

        // Handle color change for gallery
        if (
          target.dataset.name === 'Color' &&
          productGallery &&
          window.innerWidth > ProductsGrid.BREAKPOINTS.DESKTOP
        ) {
          this.updateGalleryForColor(product, target.value, productGallery);
        }

        // Update availability
        this.checkAvailability(product);
      });
    });
  }

  /**
   * Update option title when selection changes
   * @param {HTMLInputElement} input - The selected input
   */
  updateOptionTitle(input) {
    const titleElement = document.querySelector(
      `.product-modal__option-title[data-name="${input.dataset.name}"] span`
    );
    if (titleElement) {
      titleElement.textContent = input.value;
    }
  }

  /**
   * Update gallery when color changes
   * @param {Object} product - The product data
   * @param {string} colorValue - The selected color value
   * @param {Swiper} productGallery - The Swiper gallery instance
   */
  updateGalleryForColor(product, colorValue, productGallery) {
    const variant = product.variants.edges.find((variant) =>
      variant.node.title.includes(colorValue)
    );

    if (variant && variant.node.image) {
      const slides = document.querySelectorAll(
        ProductsGrid.SELECTORS.SWIPER_SLIDE
      );
      let selectedIndex = 0;

      slides.forEach((slide, index) => {
        const img = slide.querySelector('img');
        if (img && img.src === variant.node.image.url) {
          selectedIndex = index;
        }
      });

      productGallery.slideTo(selectedIndex);
    }
  }

  /**
   * Set up add to cart event listener
   * @param {Object} product - The product data
   */
  setupAddToCartListener(product) {
    const ctaButton = document.querySelector(ProductsGrid.SELECTORS.MODAL_CTA);
    if (ctaButton) {
      ctaButton.addEventListener('click', () => {
        this.addToCart(product, ctaButton);
      });
    }
  }

  /**
   * Set up "Can't find your size?" link event listener
   * @param {Object} product - The product data
   */
  setupCantFindSizeLinkListener(product) {
    const cantFindSizeLink = document.querySelector('.cant-find-size-trigger');
    if (cantFindSizeLink) {
      cantFindSizeLink.addEventListener('click', (e) => {
        e.preventDefault();
        this.openGeneralKlaviyoBISModal(product);
      });
    }
  }

  /**
   * Open general Klaviyo BIS modal (not tied to specific variant)
   * @param {Object} product - The product data
   */
  openGeneralKlaviyoBISModal(product) {
    // Reset submitting flag when opening a new modal
    this.isSubmitting = false;

    const productId = this.decodeId(product.id);
    const unavailableVariants = this.getUnavailableVariants(product);

    // Build variant options for the dropdown
    let variantOptions = '';
    unavailableVariants.forEach((variant) => {
      const variantId = this.decodeId(variant.node.id);
      variantOptions += `<option value='${variantId}'>${variant.node.title}</option>`;
    });

    // Create a general Klaviyo BIS modal for the product (without klaviyo-bis component)
    const klaviyoBisHTML = `
      <div class="general-klaviyo-modal" product-id='${productId}'>
        <div class='klaviyo-bis-modal-overlay fixed inset-0 overflow-y-auto flex items-center justify-center' aria-labelledby='modal-title' role='dialog' aria-modal='true' style='background-color: rgba(0, 0, 0, 0.5); z-index: 999999;'>
          <div id='klaviyo-bis-modal' class='relative' style='z-index: 1000000;'>
            <div class='bg-white relative border border-gray-300 shadow-lg' style='padding: 32px 18px 40px 18px; max-width: 460px;' id='container'>
              <button type='button' class='close klaviyo-bis-close' data-dismiss='modal' style='position: absolute; top: 10px; right: 15px; font-size: 24px; background: none; border: none; cursor: pointer;'>×</button>
              <h3 class='modal-title'>${product.title}</h3>
              <p>Select the size you want to be notified about when it's back in stock.</p>
              <form action='#' class='form-horizontal clearfix' id='general-klaviyo-form'>
                <div class='form-group' style='margin-bottom: 15px;'>
                  <div id='variant_select' class='col-sm-12'>
                    <label for='variant-select' style='display: block; margin-bottom: 5px; font-weight: bold;'>Size:</label>
                    <div style='position: relative;'>
                      <select id='variant-select' name='variant' class='form-control input-lg' style='width: 100%; padding: 10px 30px 10px 10px; border: 1px solid #ccc; border-radius: 4px; appearance: none; background-color: white;'>
                        ${variantOptions}
                      </select>
                      <div style='position: absolute; right: 10px; top: 50%; transform: translateY(-50%); pointer-events: none; color: #666;'>
                        <svg width='12' height='12' viewBox='0 0 12 12' fill='currentColor'>
                          <path d='M6 9L2 5h8L6 9z'/>
                        </svg>
                      </div>
                    </div>
                    <input type='hidden' id='product' value='${productId}'>
                  </div>
                </div>
                <div class='form-group' style='margin-bottom: 15px;'>
                  <div class='col-sm-12'>
                    <input required type='email' id='email' placeholder='Email' class='form-control input-lg' value=''>
                  </div>
                  <div id='newsletter_subscription' style='display: none;'>
                    <p>
                      <input type='checkbox' id='subscribe_for_newsletter' checked=''>
                      <label for='subscribe_for_newsletter' style='margin-left:10px;'>Add me to your email list.</label>
                    </p>
                  </div>
                </div>
                <div class='control-group clearfix submit-container'>
                  <button type='submit' class='btn btn-notify btn-success btn-lg col-xs-12 hover:!bg-gold'>Notify me when available</button>
                </div>
                <p class='text-sm'>
                  By clicking "Notify Me" you agree to be brainwashed by Liquid Death marketing through rare
                  (but hilarious) emails and agreeing to our
                  <a class='underline' href='/policies/terms-of-service'>Terms & Conditions</a> /
                  <a class='underline' href='/policies/privacy-policy'>Privacy Policy</a>
                </p>
                <div id='error_message' class='error_message alert alert-danger hidden'></div>
                <div id='completed_message' class='completed_message alert alert-success hidden'>
                  You're in! We'll let you know when it's back. <a href='#' class='klaviyo-bis-close'>Close</a>
                </div>
              </form>
              <p class='small-print'></p>
            </div>
          </div>
        </div>
      </div>
    `;

    // Insert the modal into the page
    document.body.insertAdjacentHTML('beforeend', klaviyoBisHTML);

    // Add event listeners for modal close functionality with a small delay to ensure DOM is ready
    setTimeout(() => {
      try {
        this.setupCantFindSizeModalCloseListeners();
      } catch (error) {
        console.error('Error in setupCantFindSizeModalCloseListeners:', error);
      }
    }, 100);
  }

  /**
   * Set up close event listeners for the general Klaviyo BIS modal
   */
  setupCantFindSizeModalCloseListeners() {
    // Find the most recently added modal (for "Can't find your size?" functionality)
    const modals = document.querySelectorAll('.general-klaviyo-modal');
    const currentModal = modals[modals.length - 1];

    if (!currentModal) {
      return;
    }

    // Close button - target only the current modal
    const closeButtons = currentModal.querySelectorAll('.klaviyo-bis-close');
    closeButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.closeGeneralModal();
      });
    });

    // Close on overlay click - target only the current modal overlay
    const modalOverlay = currentModal.querySelector(
      '.klaviyo-bis-modal-overlay'
    );
    if (modalOverlay) {
      modalOverlay.addEventListener('click', (e) => {
        if (e.target === modalOverlay) {
          this.closeGeneralModal();
        }
      });
    }

    // Close on Escape key - check if current modal is visible
    const escapeHandler = (e) => {
      if (e.key === 'Escape') {
        const visibleModal = modals[modals.length - 1];
        if (
          visibleModal &&
          !visibleModal
            .querySelector('.klaviyo-bis-modal-overlay')
            .classList.contains('hidden')
        ) {
          this.closeGeneralModal();
          document.removeEventListener('keydown', escapeHandler);
        }
      }
    };
    document.addEventListener('keydown', escapeHandler);

    // Handle notify button click only (don't handle form submit to avoid double execution)
    const notifyButton = currentModal.querySelector(
      '#general-klaviyo-form .btn-notify'
    );
    if (notifyButton) {
      // Remove any existing event listeners to prevent duplicates
      notifyButton.removeEventListener(
        'click',
        this.handleGeneralKlaviyoFormSubmit
      );
      notifyButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation(); // Stop event bubbling
        this.handleCantFindSizeKlaviyoFormSubmit(e);
      });
    }
  }

  /**
   * Close the general Klaviyo BIS modal
   */
  closeGeneralModal() {
    // Remove all general Klaviyo modals to ensure clean state
    const modalElements = document.querySelectorAll('.general-klaviyo-modal');
    modalElements.forEach((modalElement) => {
      modalElement.remove();
    });
  }

  /**
   * Handle form submission for the general Klaviyo BIS modal
   */
  async handleCantFindSizeKlaviyoFormSubmit(e) {
    // Prevent double execution
    if (this.isSubmitting) {
      return;
    }
    this.isSubmitting = true;

    const form = document.querySelector('#general-klaviyo-form');
    if (!form) {
      this.isSubmitting = false;
      return;
    }

    const emailInput = form.querySelector('#email');
    const variantSelect = form.querySelector('#variant-select');
    const productInput = form.querySelector('#product');

    if (!emailInput || !variantSelect || !productInput) {
      this.isSubmitting = false;
      return;
    }

    const email = emailInput.value.trim();
    const variantId = variantSelect.value;
    const productId = productInput.value;

    if (!email) {
      const errorMessage = form.querySelector('#error_message');
      if (errorMessage) {
        errorMessage.textContent = 'Please enter your email address';
        errorMessage.classList.remove('hidden');
      }
      this.isSubmitting = false;
      return;
    }

    if (!variantId) {
      const errorMessage = form.querySelector('#error_message');
      if (errorMessage) {
        errorMessage.textContent = 'Please select a size';
        errorMessage.classList.remove('hidden');
      }
      this.isSubmitting = false;
      return;
    }

    // Call Klaviyo API directly to avoid double execution issues
    try {
      const payload = {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                },
              },
            },
            channels: ['EMAIL'],
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: `$shopify:::$default:::${variantId}`,
              },
            },
          },
        },
      };

      const response = await fetch(
        'https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=Ntc5BJ',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: '2024-06-15',
          },
          body: JSON.stringify(payload),
        }
      );

      if (response.ok) {
        // Show success message
        const successMessage = form.querySelector('#completed_message');
        if (successMessage) {
          successMessage.classList.remove('hidden');
        }
        // Don't auto-close modal - let user close manually
      } else {
        console.error('❌ Klaviyo API call failed:', response.status);
        const errorText = await response.text();
        console.error('Error response:', errorText);
        const errorMessage = form.querySelector('#error_message');
        if (errorMessage) {
          errorMessage.textContent =
            'There was an error submitting your request. Please try again.';
          errorMessage.classList.remove('hidden');
        }
      }
    } catch (error) {
      console.error('Error calling Klaviyo API:', error);
      const errorMessage = form.querySelector('#error_message');
      if (errorMessage) {
        errorMessage.textContent =
          'There was an error submitting your request. Please try again.';
        errorMessage.classList.remove('hidden');
      }
    }

    // Reset flag after completion
    this.isSubmitting = false;
  }
  // ============================================================================
  // AVAILABILITY AND CART METHODS - Product availability checking and cart functionality
  // ============================================================================
  /**
   * Check product availability and update UI accordingly
   * @param {Object} product - The product data
   */
  checkAvailability(product) {
    let buttonText = ProductsGrid.DEFAULT_TEXT.ADD_TO_CART;
    if (product.metafields[2] != null) {
      buttonText = product.metafields[2].value;
    }

    // Remove disabled class from all size options
    const sizeOptions = document.querySelectorAll(
      ProductsGrid.SELECTORS.MODAL_SIZE_OPTION
    );
    sizeOptions.forEach((option) => {
      option.classList.remove(ProductsGrid.CSS_CLASSES.DISABLED);
      option.classList.remove('clickable-disabled');
      setSoldOutLabel(option, false);
      // Remove any existing click handlers
      option.removeEventListener('click', this.handleUnavailableVariantClick);
    });

    // Get unavailable variants
    let variants = this.getUnavailableVariants(product);

    // Mark unavailable size options as disabled and add Klaviyo BIS triggers
    if (variants.length) {
      variants.forEach((variant) => {
        const title = this.getSizeTitleFromVariant(variant);
        const sizeOption = document.querySelector(
          `.product-modal__size-option[data-name="${title}"]`
        );
        if (sizeOption) {
          sizeOption.classList.add(ProductsGrid.CSS_CLASSES.DISABLED);
          sizeOption.classList.add('clickable-disabled'); // Override pointer-events: none
          setSoldOutLabel(sizeOption, true);
          // Add click handler for unavailable variants
          sizeOption.addEventListener('click', (e) => {
            e.preventDefault();
            this.handleUnavailableVariantClick(variant, product);
          });
        }
      });
    }

    // Update CTA button based on selected variant availability
    this.updateCTABasedOnAvailability(product, buttonText);
  }

  /**
   * Get unavailable variants for the current color selection
   * @param {Object} product - The product data
   * @returns {Array} Array of unavailable variants
   */
  getUnavailableVariants(product) {
    const colorOption = document.querySelector(
      ProductsGrid.SELECTORS.MODAL_OPTION_COLOR
    );

    if (colorOption) {
      const checkedColorInput = colorOption.querySelector('input:checked');
      if (checkedColorInput) {
        return product.variants.edges.filter(
          (variant) =>
            variant.node.title.includes(checkedColorInput.value) &&
            !variant.node.availableForSale
        );
      }
    }

    return product.variants.edges.filter(
      (variant) => !variant.node.availableForSale
    );
  }

  /**
   * Get size title from variant node
   * @param {Object} variant - The variant data
   * @returns {string} The size title
   */
  getSizeTitleFromVariant(variant) {
    const colorOption = document.querySelector(
      ProductsGrid.SELECTORS.MODAL_OPTION_COLOR
    );
    let title = variant.node.title;

    if (colorOption) {
      const checkedColorInput = colorOption.querySelector('input:checked');
      if (checkedColorInput) {
        title = title
          .replace(checkedColorInput.value, '')
          .replace('/', '')
          .trim();
      }
    }

    return title;
  }

  /**
   * Update CTA button based on selected variant availability
   * @param {Object} product - The product data
   * @param {string} buttonText - The button text
   */
  updateCTABasedOnAvailability(product, buttonText) {
    const ctaButton = document.querySelector(ProductsGrid.SELECTORS.MODAL_CTA);
    if (
      !ctaButton ||
      !ctaButton.classList.contains(ProductsGrid.CSS_CLASSES.ACTIVE)
    ) {
      return;
    }

    const selectedVariants = this.getSelectedVariants(product);

    if (
      selectedVariants.length > 0 &&
      !selectedVariants[0].node.availableForSale
    ) {
      // If product has an Amazon link, keep the Amazon button instead of showing Klaviyo BIS
      if (product.metafields[1] != null && product.metafields[1].value) {
        return;
      }

      // Check if CTA button is already wrapped in a klaviyo-bis element
      const existingKlaviyoBis = ctaButton.closest('klaviyo-bis');
      if (existingKlaviyoBis) {
        return;
      }

      // Replace CTA button with Klaviyo BIS trigger
      const variantId = this.decodeId(selectedVariants[0].node.id);
      const productId = this.decodeId(product.id);

      // Only create Klaviyo BIS wrapper if we have valid IDs
      if (variantId && productId) {
        const klaviyoBisHTML = `
          <klaviyo-bis class="m-0" product-id='${productId}' variant-id='${variantId}'>
            <button class='klaviyo-bis-trigger text-center button-white flex justify-center uppercase text-base w-full'>
              ${ProductsGrid.DEFAULT_TEXT.NOTIFY_WHEN_AVAILABLE}
            </button>
            <div class='klaviyo-bis-modal-overlay fixed z-50 inset-0 overflow-y-auto hidden bg-black/50 flex items-center justify-center' aria-labelledby='modal-title' role='dialog' aria-modal='true'>
              <div id='klaviyo-bis-modal' class='relative' style='z-index: 1000000;'>
                <div class='bg-white relative border border-gray-300 shadow-lg' style='padding: 32px 18px 40px 18px; max-width: 460px;' id='container'>
                  <button type='button' class='close klaviyo-bis-close' data-dismiss='modal'>×</button>
                  <h3 class='modal-title'>${product.title}</h3>
                  <p>Register to receive a notification when this item comes back in stock.</p>
                  <form action='#' class='form-horizontal clearfix'>
                    <div class='form-group' style='margin-bottom: 15px;'>
                      <div id='variant_select' class='col-sm-12' style='display: none;'>
                        <input type='hidden' id='product' value='${variantId}'>
                        <select id='variants' class='selectpicker form-control input-lg'>
                          <option value='${variantId}'>${selectedVariants[0].node.title}</option>
                        </select>
                      </div>
                    </div>
                    <div class='form-group' style='margin-bottom: 15px;'>
                      <div class='col-sm-12'>
                        <input required type='email' id='email' placeholder='Email' class='form-control input-lg' value=''>
                      </div>
                      <div id='newsletter_subscription' style='display: none;'>
                        <p>
                          <input type='checkbox' id='subscribe_for_newsletter' checked=''>
                          <label for='subscribe_for_newsletter' style='margin-left:10px;'>Add me to your email list.</label>
                        </p>
                      </div>
                    </div>
                    <div class='control-group clearfix submit-container'>
                      <button type='submit' class='btn btn-notify btn-success btn-lg col-xs-12 hover:!bg-gold'>Notify me when available</button>
                    </div>
                    <p class='text-sm'>
                      By clicking "Notify Me" you agree to be brainwashed by Liquid Death marketing through rare
                      (but hilarious) emails and agreeing to our
                      <a class='underline text-[#8a6d35]' href='/policies/terms-of-service'>Terms & Conditions</a> /
                      <a class='underline text-[#8a6d35]' href='/policies/privacy-policy'>Privacy Policy</a>
                    </p>
                    <div id='error_message' class='error_message alert alert-danger hidden'></div>
                    <div id='completed_message' class='completed_message alert alert-success hidden'>
                      You're in! We'll let you know when it's back. <a href='#' class='klaviyo-bis-close'>Close</a>
                    </div>
                  </form>
                  <p class='small-print'></p>
                </div>
              </div>
            </div>
          </klaviyo-bis>
        `;

        ctaButton.outerHTML = klaviyoBisHTML;
      } else {
        // Fallback to regular text if IDs are invalid
        ctaButton.textContent = ProductsGrid.DEFAULT_TEXT.NOTIFY_WHEN_AVAILABLE;
      }
    } else {
      ctaButton.textContent = buttonText;
      ctaButton.classList.add(ProductsGrid.CSS_CLASSES.ACTIVE);
    }
  }

  /**
   * Get selected variants based on current option selections
   * @param {Object} product - The product data
   * @returns {Array} Array of selected variants
   */
  getSelectedVariants(product) {
    let selectedVariants = product.variants.edges;
    const allCheckedInputs = document.querySelectorAll(
      '.product-modal__options-wrap input:checked'
    );

    // Filter to only include inputs that are actual product options (have names starting with 'option-')
    const checkedInputs = Array.from(allCheckedInputs).filter(
      (input) => input.name && input.name.startsWith('option-')
    );

    checkedInputs.forEach((input, index) => {
      selectedVariants = selectedVariants.filter((variant) => {
        const titleParts = variant.node.title.split(' / ');
        const titlePart = titleParts[index];
        return titlePart && titlePart.trim() === input.value;
      });
    });

    return selectedVariants;
  }

  /**
   * Decode Shopify ID from GraphQL format
   * @param {string} id - The Shopify GraphQL ID
   * @returns {number} The decoded numeric ID
   */
  decodeId(id) {
    if (!id || typeof id !== 'string') {
      return null;
    }
    const shopifyId = id.split('/').pop();
    return parseInt(shopifyId);
  }

  /**
   * Add product to cart
   * @param {Object} product - The product data
   * @param {HTMLElement} button - The clicked button
   */
  async addToCart(product, button) {
    if (!button.classList.contains(ProductsGrid.CSS_CLASSES.ACTIVE)) {
      return;
    }

    try {
      const selectedVariants = this.getSelectedVariants(product);

      if (selectedVariants.length === 0) {
        console.error('No variant selected');
        return;
      }

      const variantId = this.decodeId(selectedVariants[0].node.id);

      if (!variantId) {
        console.error('Invalid variant ID for add to cart');
        return;
      }

      const formData = {
        items: [
          {
            id: variantId,
            quantity: 1,
          },
        ],
      };

      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Track analytics if allowed
      if (window.Shopify.customerPrivacy.marketingAllowed()) {
        window.amzn?.('trackEvent', 'AddToShoppingCart');
      }

      // Close modal
      const modal = document.querySelector(
        ProductsGrid.SELECTORS.PRODUCT_MODAL
      );
      if (modal) {
        modal.remove();
      }
    } catch (error) {
      console.error('Error adding product to cart:', error);
      this.handleAddToCartError(error);
    }
  }

  /**
   * Handle add to cart errors
   * @param {Error} error - The error that occurred
   */
  handleAddToCartError(error) {
    console.error('Failed to add product to cart:', error);
    // Could add user-facing error handling here
  }

  /**
   * Handle click on unavailable variant size option
   * Opens Klaviyo BIS modal for that specific variant
   */
  handleUnavailableVariantClick(variant, product) {
    const productId = this.decodeId(product.id);
    const variantId = this.decodeId(variant.node.id);

    // Create a specific Klaviyo BIS modal for this variant
    const klaviyoBisHTML = `
      <div class="general-klaviyo-modal" product-id='${productId}'>
        <div class='klaviyo-bis-modal-overlay fixed inset-0 overflow-y-auto flex items-center justify-center' aria-labelledby='modal-title' role='dialog' aria-modal='true' style='background-color: rgba(0, 0, 0, 0.5); z-index: 999999;'>
          <div id='klaviyo-bis-modal' class='relative' style='z-index: 1000000;'>
            <div class='bg-white relative border border-gray-300 shadow-lg' style='padding: 32px 18px 40px 18px; max-width: 460px;' id='container'>
              <button type='button' class='close klaviyo-bis-close' data-dismiss='modal' style='position: absolute; top: 10px; right: 15px; font-size: 24px; background: none; border: none; cursor: pointer;'>×</button>
              <h3 class='modal-title'>${product.title}</h3>
              <p>Get notified when <strong>${variant.node.title}</strong> is back in stock.</p>
              <form action='#' class='form-horizontal clearfix' id='general-klaviyo-form'>
                <div class='form-group' style='margin-bottom: 15px;'>
                  <div class='col-sm-12'>
                    <input required type='email' id='email' placeholder='Email' class='form-control input-lg' value=''>
                  </div>
                  <div id='newsletter_subscription' style='display: none;'>
                    <p>
                      <input type='checkbox' id='subscribe_for_newsletter' checked=''>
                      <label for='subscribe_for_newsletter' style='margin-left:10px;'>Add me to your email list.</label>
                    </p>
                  </div>
                </div>
                <div class='control-group clearfix submit-container'>
                  <button type='submit' class='btn btn-notify btn-success btn-lg col-xs-12 hover:!bg-gold'>Notify me when available</button>
                </div>
                <p class='text-sm'>
                  By clicking "Notify Me" you agree to be brainwashed by Liquid Death marketing through rare
                  (but hilarious) emails and agreeing to our
                  <a class='underline' href='/policies/terms-of-service'>Terms & Conditions</a> /
                  <a class='underline' href='/policies/privacy-policy'>Privacy Policy</a>
                </p>
                <div id='error_message' class='error_message alert alert-danger hidden'></div>
                <div id='completed_message' class='completed_message alert alert-success hidden'>
                  You're in! We'll let you know when it's back. <a href='#' class='klaviyo-bis-close'>Close</a>
                </div>
              </form>
              <p class='small-print'></p>
            </div>
          </div>
        </div>
      </div>
    `;

    // Insert the modal into the page
    document.body.insertAdjacentHTML('beforeend', klaviyoBisHTML);

    // Add event listeners for modal close functionality
    this.setupGeneralModalCloseListeners(variantId, productId);
  }

  /**
   * Sets up event listeners for the general Klaviyo BIS modal
   */
  setupGeneralModalCloseListeners(variantId, productId) {
    // Find the specific modal we just created
    const currentModal = document.querySelector(
      '.general-klaviyo-modal[product-id="' + productId + '"]'
    );
    if (!currentModal) return;

    // Close button - target only the current modal
    const closeButtons = currentModal.querySelectorAll('.klaviyo-bis-close');
    closeButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.closeGeneralModal();
      });
    });

    // Close on overlay click - target only the current modal overlay
    const modalOverlay = currentModal.querySelector(
      '.klaviyo-bis-modal-overlay'
    );
    if (modalOverlay) {
      modalOverlay.addEventListener('click', (e) => {
        if (e.target === modalOverlay) {
          this.closeGeneralModal();
        }
      });
    }

    // Close on Escape key - check if current modal is visible
    const escapeHandler = (e) => {
      if (e.key === 'Escape') {
        const visibleModal = document.querySelector(
          '.general-klaviyo-modal[product-id="' + productId + '"]'
        );
        if (
          visibleModal &&
          !visibleModal
            .querySelector('.klaviyo-bis-modal-overlay')
            .classList.contains('hidden')
        ) {
          this.closeGeneralModal();
          document.removeEventListener('keydown', escapeHandler);
        }
      }
    };
    document.addEventListener('keydown', escapeHandler);

    // Handle notify button click - target only the current modal
    const notifyButton = currentModal.querySelector(
      '#general-klaviyo-form .btn-notify'
    );
    if (notifyButton) {
      notifyButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleGeneralKlaviyoFormSubmit(e, variantId, productId);
      });
    }
  }

  /**
   * Close the general Klaviyo BIS modal
   */
  closeGeneralModal() {
    // Remove all general Klaviyo modals to ensure clean state
    const modalElements = document.querySelectorAll('.general-klaviyo-modal');
    modalElements.forEach((modalElement) => {
      modalElement.remove();
    });
  }

  /**
   * Handle form submission for the general Klaviyo BIS modal
   */
  async handleGeneralKlaviyoFormSubmit(e, variantId, productId) {
    const form = document.querySelector('#general-klaviyo-form');
    if (!form) return;

    const emailInput = form.querySelector('#email');

    if (!emailInput) {
      console.error('Email input not found');
      return;
    }

    const email = emailInput.value.trim();

    if (!email) {
      const errorMessage = form.querySelector('#error_message');
      if (errorMessage) {
        errorMessage.textContent = 'Please enter your email address';
        errorMessage.classList.remove('hidden');
      }
      return;
    }

    try {
      const payload = {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                },
              },
            },
            channels: ['EMAIL'],
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: `$shopify:::$default:::${variantId}`,
              },
            },
          },
        },
      };

      const response = await fetch(
        'https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=Ntc5BJ',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: '2024-06-15',
          },
          body: JSON.stringify(payload),
        }
      );

      if (response.ok) {
        // Show success message
        const successMessage = form.querySelector('#completed_message');
        if (successMessage) {
          successMessage.classList.remove('hidden');
        }
      } else {
        console.error('❌ Klaviyo API call failed:', response.status);
        const errorText = await response.text();
        console.error('Error response:', errorText);
        const errorMessage = form.querySelector('#error_message');
        if (errorMessage) {
          errorMessage.textContent =
            'There was an error submitting your request. Please try again.';
          errorMessage.classList.remove('hidden');
        }
      }
    } catch (error) {
      console.error('Error calling Klaviyo API:', error);
      const errorMessage = form.querySelector('#error_message');
      if (errorMessage) {
        errorMessage.textContent =
          'There was an error submitting your request. Please try again.';
        errorMessage.classList.remove('hidden');
      }
    }
  }
}
customElements.define('products-grid', ProductsGrid);
